import { useState, useMemo, useRef,useCallback } from "react"
import {
  Eye,
  Trash2,
  FileText,
  FileImage,
  FileType2,
  Presentation,
  File,
  LoaderCircle,
  CircleAlert,
  CircleCheck,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  X,
  ChevronUp,
  ChevronDown
} from "lucide-react"

import { Badge } from "components/ui/badge_v5"
import { Button } from "components/ui/button_v3"
import { Checkbox } from "components/ui/checkbox_v2"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table_v3"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip_v2"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select_v4"
import { ResourceFilters } from "./ResourceFilters"
import {
  getFileTypeLabel,
  getFileTypeIcon,
  formatFileSize,
  formatDate,
} from "services/utils"
import { cn } from "lib/utils"
import { useThemeStore } from "context/useThemeStore"
import localization from "services/localization"
import { SmartTooltip } from "./SmartTooltip"

function FileIcon({ fileType, className }) {
  const icon = getFileTypeIcon(fileType)
  const iconClass = cn("size-4", className)

  switch (icon) {
    case "pdf":
      return <FileText className={cn(iconClass, "stroke-red-600")} />
    case "image":
      return <FileImage className={cn(iconClass, "stroke-blue-500")} />
    case "word":
      return <FileType2 className={cn(iconClass, "stroke-blue-500")} />
    case "ppt":
      return <Presentation className={cn(iconClass, "stroke-yellow-500")} />
    case "text":
      return <FileText className={cn(iconClass, "stroke-gray-400")} />
    default:
      return <File className={cn(iconClass, "stroke-gray-400")} />
  }
}

export function ResourceTable({
  resources,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  onView,
  onDelete,
  onRemoveSelected,
  allUsers = [],
  isRoot = false,
  userId
}) {
  const [searchQuery, setSearchQuery] = useState("")
  const [fileTypeFilter, setFileTypeFilter] = useState("all")
  const [dateRange, setDateRange] = useState(undefined)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortConfig, setSortConfig] = useState({
  key: null,
  direction: "asc",
})
  const { theme } = useThemeStore()
  const tableContainerRef = useRef(null)

  const userMap = useMemo(() => {
  const map = {}
  allUsers.forEach(user => {
    map[user.id] = user.name
  })
  return map
}, [allUsers])

function isResourceSelectable(resource) {
  if (!resource) return false

  return resource.to_rag
    ? resource?.gfs?.status === "success"
    : resource?.gcs?.status === "success"
}

    // .slice()
    // .sort((a, b) =>
    //   (a.originalName || "").localeCompare(b.originalName || "", undefined, {
    //     sensitivity: "base",
    //   })
    // )
    
const filteredResources = useMemo(() => {
  const filtered = resources.filter((resource) => {
    const matchesSearch =
      !searchQuery ||
      resource.originalName
        .toLowerCase()
        .includes(searchQuery.toLowerCase())

    const matchesType =
      fileTypeFilter === "all" || resource.fileType === fileTypeFilter

    let matchesDate = true
    if (dateRange?.from) {
      const created = new Date(resource.created_at)
      matchesDate = created >= dateRange.from

      if (dateRange.to) {
        const endOfDay = new Date(dateRange.to)
        endOfDay.setHours(23, 59, 59, 999)
        matchesDate = matchesDate && created <= endOfDay
      }
    }

    return matchesSearch && matchesType && matchesDate
  })

  if (!sortConfig.key) return filtered

  return [...filtered].sort((a, b) => {
    let aValue
    let bValue

    switch (sortConfig.key) {
      case "name":
        aValue = a.originalName || ""
        bValue = b.originalName || ""
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue, undefined, { sensitivity: "base" })
          : bValue.localeCompare(aValue, undefined, { sensitivity: "base" })

      case "owner":
        aValue = userMap[a.user_id] || ""
        bValue = userMap[b.user_id] || ""
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue, undefined, { sensitivity: "base" })
          : bValue.localeCompare(aValue, undefined, { sensitivity: "base" })

      case "created":
        aValue = new Date(a.created_at).getTime()
        bValue = new Date(b.created_at).getTime()
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue

      default:
        return 0
    }
  })
}, [resources, searchQuery, fileTypeFilter, dateRange, sortConfig, userMap])

  // Reset to page 1 when filters change
  const totalPages = Math.max(1, Math.ceil(filteredResources.length / pageSize))
  const safeCurrentPage = Math.min(currentPage, totalPages)
  const paginatedResources = filteredResources.slice(
    (safeCurrentPage - 1) * pageSize,
    safeCurrentPage * pageSize
  )

  const selectableFiltered = filteredResources.filter(isResourceSelectable)

  const allFilteredSelected =
    selectableFiltered.length > 0 &&
    selectableFiltered.every((r) => selectedIds.includes(r.id))

  const someFilteredSelected =
    selectableFiltered.some((r) => selectedIds.includes(r.id)) &&
    !allFilteredSelected

  function toggleAll() {
    if (!onSelectionChange) return
    if (allFilteredSelected) {
      const idsToRemove = new Set(selectableFiltered.map((r) => r.id))
      onSelectionChange(selectedIds.filter((id) => !idsToRemove.has(id)))
    } else {
      const existing = new Set(selectedIds)
      const toAdd = selectableFiltered.filter((r) => !existing.has(r.id)).map((r) => r.id)
      onSelectionChange([...selectedIds, ...toAdd])
    }
  }

  function toggleOne(id) {
    if (!onSelectionChange) return
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((i) => i !== id))
    } else {
      onSelectionChange([...selectedIds, id])
    }
  }

const scrollToResource = useCallback(
  (id) => {
    const index = filteredResources.findIndex((r) => r.id === id)
    if (index === -1) return

    // 1️⃣ Move to correct page first
    const targetPage = Math.floor(index / pageSize) + 1
    setCurrentPage(targetPage)

    // 2️⃣ Wait for DOM update
    setTimeout(() => {
      const container = tableContainerRef.current
      if (!container) return

      const row = container.querySelector(
        `[data-resource-id="${id}"]`
      )

      if (row) {
        row.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })

       row.classList.add(
  "bg-sky-100",
  "transition-colors",
  "duration-700"
)


        setTimeout(() => {
           row.classList.remove("bg-sky-100")
        }, 1500)
      }
    }, 100)
  },
  [filteredResources, pageSize]
)

function handleSort(key) {
  setCurrentPage(1)

  setSortConfig((prev) => {
    if (prev.key === key) {
      return {
        key,
        direction: prev.direction === "asc" ? "desc" : "asc",
      }
    }

    return {
      key,
      direction: "asc",
    }
  })
}

function SortableHeader({ label, sortKey, className = "" }) {
  const isActive = sortConfig.key === sortKey

  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => handleSort(sortKey)}
        className="inline-flex items-center gap-1 font-medium hover:text-foreground transition-colors"
      >
        <span>{label}</span>

        {isActive ? (
          sortConfig.direction === "asc" ? (
            <ChevronUp className="size-4" />
          ) : (
            <ChevronDown className="size-4" />
          )
        ) : (
          <ArrowUpDown className="size-4 opacity-50" />
        )}
      </button>
    </TableHead>
  )
}

const select_bg =
  theme === "dark"
    ? "bg-[var(--card-bg)] text-white border-border"
    : "bg-white text-foreground border-border";

  return (
    <div className="flex flex-col gap-4 h-[calc(100vh-230px)]">
      <ResourceFilters
        searchQuery={searchQuery}
        onSearchChange={(v) => { setSearchQuery(v); setCurrentPage(1) }}
        fileTypeFilter={fileTypeFilter}
        onFileTypeChange={(v) => { setFileTypeFilter(v); setCurrentPage(1) }}
        dateRange={dateRange}
        onDateRangeChange={(v) => { setDateRange(v); setCurrentPage(1) }}
        theme={theme}
      />

       {selectedIds.length > 0 && (
            <div
  className={cn(
    "rounded-lg border p-3",
    select_bg
  )}
>
              <p className="text-xs font-medium text-muted-foreground mb-2 description_text">
                {localization.selected_resources || "Selected Resources"}
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedIds.map((r) => {
                  var resource = resources.find((res) => res.id === r)
                  if (!resource) return
                  return(
                  <Badge
                    key={r.id}
                    variant="secondary"
                    className="gap-1.5 pr-1 cursor-pointer hover:bg-secondary/80 transition-colors"
                    onClick={() => scrollToResource(r)}
                  >
                    <span className="max-w-[150px] truncate">{resource.originalName}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveSelected(r);
                      }}
                      className="rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                  )
})}
              </div>
            </div>
          )}

    <div className="flex flex-col flex-1 min-h-0">
  <div
    ref={tableContainerRef}
    className="flex-1 min-h-0 overflow-y-auto rounded-lg border bg-card"
  >
          <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {selectable && (
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={allFilteredSelected ? true : someFilteredSelected ? "indeterminate" : false}
                    onCheckedChange={toggleAll}
                    aria-label="Select all resources"
                  />
                </TableHead>
              )}
              <SortableHeader
  label={localization.name || "Name"}
  sortKey="name"
/>
              {isRoot && (
  <SortableHeader
    label={localization.owner || "Owner"}
    sortKey="owner"
    className="hidden md:table-cell"
  />
)}
              <TableHead className="hidden sm:table-cell">{localization.type || "Type"}</TableHead>
              <TableHead className="hidden md:table-cell">{localization.size || "Size"}</TableHead>
              <TableHead className="hidden md:table-cell">{localization.visiblity || "Visibility"}</TableHead>
              {/* <TableHead className="hidden lg:table-cell">Status</TableHead> */}
              <TableHead className="hidden sm:table-cell">{localization.status || "Status"}</TableHead>
             <SortableHeader
  label={localization.created || "Created"}
  sortKey="created"
  className="hidden sm:table-cell"
/>
              <TableHead className="text-right">{localization.actions || "Actions"}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedResources.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={selectable ? 9 : 8}
                  className="h-32 text-center text-muted-foreground"
                >
                  {localization.no_resource_found || "No resources found matching your filters."}
                </TableCell>
              </TableRow>
            ) : (
              paginatedResources.map((resource) => {
                const isSelected = selectedIds.includes(resource.id)
                const fileSize = resource.file_size || resource.gfs?.data?.response?.sizeBytes
                const canSelect = isResourceSelectable(resource)
                const to_rag = resource.to_rag
                const gfsStatus = resource?.gfs?.status || "unknown"
                const gcsStatus = resource?.gcs?.status || "unknown"
                const status = to_rag ? gfsStatus : gcsStatus

                return (
                  <TableRow
                    data-resource-id={resource.id}
                    key={resource.id}
                    data-state={isSelected ? "selected" : undefined}
                    className={cn(
                      "cursor-default transition-colors",
                      selectable && canSelect && "cursor-pointer",
                      !canSelect && "opacity-60",
                      isSelected && theme === "light" ?"bg-sky-50 hover:bg-sky-100": isSelected && theme === "dark" ? "bg-[#4F6F99] hover:bg-[#3F5F88]" : "",
                    )}
                    onClick={selectable && canSelect ? () => toggleOne(resource.id) : undefined}
                  >
                    {selectable && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          disabled={!canSelect}
                          onCheckedChange={() => canSelect && toggleOne(resource.id)}
                          aria-label={
                            canSelect
                              ? `Select ${resource.originalName}`
                              : `${resource.originalName} is not available for selection`
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <FileIcon fileType={resource.fileType} />
                        <SmartTooltip
                          theme={theme}
                          className="font-medium max-w-[200px] sm:max-w-[380px]"
                          >
                        {resource.originalName}
                    </SmartTooltip>
                      </div>
                    </TableCell>
                    {isRoot && (
                      <TableCell className="hidden md:table-cell">
                        <span className="text-muted-foreground truncate max-w-[180px] inline-block">
                          {userMap[resource.user_id] || "Unknown"}
                        </span>
                      </TableCell>
                    )}
                    <TableCell className="hidden sm:table-cell">
                      <span className="text-muted-foreground">
                        {getFileTypeLabel(resource.fileType)}
                      </span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-muted-foreground tabular-nums">
                        {fileSize ? formatFileSize(fileSize) : "-"}
                      </span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge
                        variant={
                          resource.visibility_scope === "public"
                            ? "default"
                            : resource.visibility_scope === "private"
                            ? "secondary"
                            : "outline"
                        }
                      >
                        {resource.visibility_scope}
                      </Badge>
                    </TableCell>
                    {/* <TableCell className="hidden lg:table-cell">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={cn(
                            "size-2 rounded-full",
                            resource.enabled
                              ? "bg-chart-2"
                              : "bg-muted-foreground"
                          )}
                        />
                        <span className="text-muted-foreground text-xs">
                          {resource.enabled ? "Active" : "Disabled"}
                        </span>
                      </div>
                    </TableCell> */}
                  <TableCell className="hidden sm:table-cell">
                      {status === "success" ? (
                      <div className="flex items-center gap-1.5">
  <CircleCheck className="size-3.5 stroke-green-600" />
  <span className="text-xs font-medium">{localization.ready || "Ready"}</span>
</div>
                      ) : status === "in_progress" ? (
                       <div className="flex items-center gap-1.5">
  <LoaderCircle className="size-3.5 stroke-[#ffba00] inline-block animate-spin" />
  <span className="text-xs text-[#ffba00] font-medium">{ localization.processing || "Processing"}</span>
</div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <CircleAlert className="size-3.5 stroke-red-600" />
                          <span className="text-xs text-red-600 font-medium">{localization.failed || "Failed"}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span className="text-muted-foreground tabular-nums">
                        {formatDate(resource.created_at)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => onView?.(resource)}
                                aria-label={`View ${resource.originalName}`}
                                className="hover:bg-green-50"
                              >
                            <Eye className="size-4 stroke-green-600" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent theme={theme}>{ localization.view_document || "View document"}</TooltipContent>
                          </Tooltip>
                            <Tooltip>
                            <TooltipTrigger asChild>
                                  <span>
                              <Button
                                disabled={!isRoot && userId !== resource.user_id}
                                variant="ghost"
                                size="icon-sm"
                                onClick={() =>{
                                    if (!isRoot && userId !== resource.user_id) return;
                                  onDelete?.(resource)}}
            className="hover:bg-red-50"
                                aria-label={`Delete ${resource.originalName}`}
                              >
            <Trash2 className="size-4 stroke-red-600" />
                              </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent theme={theme}>
                               {isRoot || userId === resource.user_id
      ? localization.delete_document || "Delete document"
      : localization.unable_to_delete || "Unable to delete"}
                              </TooltipContent>
                          </Tooltip>
                          
                        </div>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm text-muted-foreground px-1">
        <span>
          {filteredResources.length} {localization.resources || "Resources"}
          {selectable && selectedIds.length > 0 && (
            <span className="text-foreground font-medium">
              {" "}&middot; {selectedIds.length} {localization.selected || "Selected"}
            </span>
          )}
        </span>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs whitespace-nowrap page-subtitle">{localization.rows_per_page || "Rows per page"}</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1) }}
            >
              <SelectTrigger className="h-8 w-[64px] page-subtitle">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <span className="text-xs tabular-nums whitespace-nowrap page-subtitle">
            {localization.page || "Page"} {safeCurrentPage} {localization.of || "of"} {totalPages}
          </span>

          <div className="flex items-center gap-1 page-subtitle">
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              onClick={() => setCurrentPage(1)}
              disabled={safeCurrentPage <= 1}
              aria-label="First page"
            >
              <ChevronsLeft className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={safeCurrentPage <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={safeCurrentPage >= totalPages}
              aria-label="Next page"
            >
              <ChevronRight className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              onClick={() => setCurrentPage(totalPages)}
              disabled={safeCurrentPage >= totalPages}
              aria-label="Last page"
            >
              <ChevronsRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
